import React, { useEffect, useState, useCallback } from "react";
import OfferItem from "../components/OfferItem";
import axios from "axios";
import { GET_BASE_URL } from "../utils/Constants";
import '../index.css';
import ProgressStepper from "../components/ProgressStepper";
import InstallFlow from "./InstallFlow";
import useConfig from "../hook/useConfig";
import { Loader2, CheckCircle } from "lucide-react";
import androidIcon from "../assets/res/android_white.svg";
import appleIcon from "../assets/res/apple_white.svg";


const OffersScreen = ({ platform, nicheName, imageUrl, offersList, size, onClose, skipStepper = false }) => {


    const [offers, setOffers] = useState([]);
    const [offersLoading, setOffersLoading] = useState(true);
    const [phase, setPhase] = useState(skipStepper ? "offers" : "loading"); // loading | exiting | offers
    const [height, setHeight] = useState(window.innerHeight);
    const [changeISP, setChangeISP] = useState({
        isValid: true,
        msg: ""
    });

    const { config, loading: configLoading } = useConfig();
    const offerMode = config?.offer_mode === "api" ? "api" : "iframe";


    useEffect(() => {
        const updateHeight = () => setHeight(window.innerHeight);
        window.addEventListener("resize", updateHeight);

        fetchOffers();

        return () => window.removeEventListener("resize", updateHeight);

    }, []);


    const fetchOffers = async () => {
        try {
            const response = await axios.get(GET_BASE_URL() + "offers.php", {
                params: { niche_name: nicheName }
            });
            setOffers(response.data);
        } catch (error) {
            // silently fail — offers will show empty state
        } finally {
            setOffersLoading(false);
        }
    };



    const onStepperCompleated = useCallback(() => {
        setPhase("exiting");
        setTimeout(() => setPhase("offers"), 350);
    }, []);

    const handleUnlock = useCallback(() => {
        const ext = platform === "ios" ? "ipa" : "apk";
        const url = GET_BASE_URL() + ext + "/" + imageUrl;
        localStorage.removeItem(`offer_progress_${nicheName}`);
        window.open(url, "_blank");
    }, [platform, imageUrl, nicheName]);

    return (
        <div
            class="relative z-20 overflow-y-scroll fade-in"
            aria-labelledby="modal-title"
            role="dialog"
            aria-modal="true"
        >


            <div
                style={{ height: height }}
                className={`fixed inset-0 transition-opacity bg-gradient-to-r from-semi-blue to-light-blue flex flex-col ${phase === "offers" ? "overflow-hidden" : "overflow-y-auto"}`}
                aria-hidden="true"
            >
                {/* Stepper phase — scrollable, padded */}
                {(phase === "loading" || phase === "exiting") && (
                    <div className="w-full max-w-md md:max-w-lg lg:max-w-2xl mx-auto p-[12px]">
                        <img
                            className="h-[64px] w-[64px] bg-white mx-auto mt-[16px] rounded-xl fade-in"
                            src={GET_BASE_URL() + "images/" + imageUrl}
                        />
                        <div className="semiBold text-[22px] mt-[8px] text-white mx-auto text-center fade-in">{nicheName}</div>
                        <div className={phase === "exiting" ? "card-exit" : ""}>
                            <ProgressStepper onComplete={onStepperCompleated} />
                        </div>
                    </div>
                )}

                {/* Offers phase — full height, no padding */}
                {phase === "offers" && (
                    <div className="flex-1 flex flex-col card-enter min-h-0">
                        {configLoading ? (
                            <div className="flex-1 flex flex-col items-center justify-center gap-3">
                                <Loader2 className="w-8 h-8 text-white animate-spin" />
                                <p className="text-white/60 text-sm medium">Loading offers...</p>
                            </div>
                        ) : offerMode === "api" ? (
                            <div className="relative h-full flex flex-col" style={{ background: "linear-gradient(135deg, #012a4a 0%, #013a63 100%)" }}>
                                {/* Header */}
                                <div className="flex-shrink-0 px-4 py-3 border-b border-white/10">
                                    <div className="flex items-center gap-3">
                                        <img
                                            src={GET_BASE_URL() + "images/" + imageUrl}
                                            alt={nicheName}
                                            className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="semiBold text-white text-[15px] truncate">{nicheName}</div>
                                            <div className="flex items-center gap-3 mt-0.5">
                                                {size && <span className="text-white/50 text-xs medium">📦 {size}</span>}
                                                {platform && (
                                                    <span className="flex items-center gap-1 text-white/50 text-xs medium">
                                                        <img src={platform === "ios" ? appleIcon : androidIcon} alt="" className="w-3 h-3" />
                                                        {platform === "ios" ? "iOS" : "Android"}
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                        <button
                                            onClick={onClose}
                                            className="flex-shrink-0 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
                                        >
                                            <span className="text-white/60 text-sm">✕</span>
                                        </button>
                                    </div>
                                    <div
                                        className="flex items-center gap-2 rounded-xl px-3 py-2 mt-2"
                                        style={{ background: "rgba(34,197,94,0.12)", border: "1px solid rgba(34,197,94,0.25)" }}
                                    >
                                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                                        <div className="min-w-0">
                                            <div className="semiBold text-green-400 text-[13px]">
                                                Your {platform === "ios" ? "IPA" : "APK"} is ready!
                                            </div>
                                            <div className="medium text-white/60 text-[11px]">
                                                Complete one quick step below to start your download.
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Offer list */}
                                <div className="flex-1 overflow-y-auto px-3 py-3">
                                    {offersLoading ? (
                                        <div className="flex flex-col items-center justify-center gap-3 mt-10">
                                            <Loader2 className="w-7 h-7 text-light-blue animate-spin" />
                                            <p className="text-white/60 text-sm medium">Loading offers...</p>
                                        </div>
                                    ) : offers.length ? (
                                        offers.map((item) => (
                                            <OfferItem key={item.id} offer={item} />
                                        ))
                                    ) : (
                                        <p className="text-white/60 text-sm medium text-center mt-10">
                                            No tasks available right now. Please try again later.
                                        </p>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <InstallFlow
                                nicheName={nicheName}
                                imageUrl={imageUrl}
                                platform={platform}
                                size={size}
                                onUnlock={handleUnlock}
                                onClose={onClose}
                            />
                        )}
                    </div>
                )}
            </div>


            {!changeISP.isValid &&
                <div className="fixed inset-0 bg-gray-800  bg-opacity-75  mx-auto flex items-center justify-center px-[16px]">
                    <div className="bg-white p-6 rounded-2xl shadow-lg max-w-md w-full text-center">
                        <h2 className="text-xl semiBold text-gray-800 mb-4">
                            Connectivity Issue
                        </h2>
                        <p className="text-gray-800 medium mb-6">
                            {changeISP.msg}
                        </p>
                        <button
                            className="bg-semi-blue text-white py-2 medium px-4 rounded-3xl hover:bg-blue-600 transition-colors w-full"
                            onClick={() => setChangeISP(
                                {
                                    isValid: true,
                                    msg: ""
                                }
                            )}
                        >
                            Understood
                        </button>
                    </div>
                </div>
            }
        </div>
    )

}

export default OffersScreen;